import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import { Vector2, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateGround } from '@babylonjs/core/Meshes/Builders/groundBuilder';
import '@babylonjs/core/Rendering/edgesRenderer';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Effect } from '@babylonjs/core/Materials/effect';
import { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import '@babylonjs/core/Layers/effectLayerSceneComponent';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';

import { PALETTE } from '@observant/shared';
import type { InterferenceState } from '../sim/interference.js';
import { DEFAULT_INTERFERENCE_CONFIG as POOL } from '../sim/interference.js';
import { createQubitMesh, type QubitMesh } from './qubitMesh.js';

import poolFrag from '../shaders/interference.fragment.glsl?raw';

const VERTEX = /* glsl */ `
precision highp float;
attribute vec3 position;
uniform mat4 world;
uniform mat4 worldViewProjection;
varying vec2 vWorld;
void main() {
  vec4 wp = world * vec4(position, 1.0);
  vWorld = wp.xz;
  gl_Position = worldViewProjection * vec4(position, 1.0);
}`;

export class InterferenceScene {
  readonly engine: Engine;
  readonly scene: Scene;
  private camera: UniversalCamera;
  private qubit: QubitMesh;
  private poolMat: ShaderMaterial;
  private peekX = 0;

  constructor(engine: Engine) {
    this.engine = engine;
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.008, 0.012, 0.025, 1);

    this.camera = new UniversalCamera('cam', new Vector3(0, 6, 1), this.scene);
    this.camera.fov = 0.95;
    this.camera.minZ = 0.1;
    this.camera.maxZ = 300;

    const glow = new GlowLayer('glow', this.scene, { blurKernelSize: 24 });
    glow.intensity = 0.8;
    const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), this.scene);
    hemi.intensity = 0.4;

    // The interference pool (shader floor).
    Effect.ShadersStore['interfVertexShader'] = VERTEX;
    Effect.ShadersStore['interfFragmentShader'] = `precision highp float;\n${poolFrag}`;
    this.poolMat = new ShaderMaterial(
      'pool',
      this.scene,
      { vertex: 'interf', fragment: 'interf' },
      {
        attributes: ['position'],
        uniforms: ['world', 'worldViewProjection', 'time', 'phi', 'k', 'omega', 's1', 's2'],
      },
    );
    this.poolMat.setFloat('k', POOL.k);
    this.poolMat.setFloat('omega', POOL.omega);
    this.poolMat.setVector2('s1', new Vector2(POOL.sources[0].x, POOL.sources[0].z));
    this.poolMat.setVector2('s2', new Vector2(POOL.sources[1].x, POOL.sources[1].z));
    const w = POOL.xMax - POOL.xMin;
    const d = POOL.zMax - POOL.zMin;
    const pool = CreateGround('pool', { width: w + 6, height: d + 6 }, this.scene);
    pool.position.set(0, 0, (POOL.zMin + POOL.zMax) / 2);
    pool.material = this.poolMat;

    const dark = new StandardMaterial('dark', this.scene);
    dark.diffuseColor = new Color3(0.05, 0.06, 0.09);
    dark.emissiveColor = new Color3(0.015, 0.02, 0.03);

    // Wave-source pylons.
    for (let i = 0; i < POOL.sources.length; i++) {
      const src = POOL.sources[i]!;
      const post = CreateCylinder(`src-${i}`, { diameter: 0.6, height: 3.2 }, this.scene);
      post.position.set(src.x, 1.6, src.z);
      const m = new StandardMaterial(`srcMat-${i}`, this.scene);
      m.emissiveColor = Color3.FromHexString(PALETTE.neonMagenta);
      m.disableLighting = true;
      post.material = m;
    }

    // EXIT between the sources.
    const frame = CreateBox('exit', { width: 2.4, height: 3.4, depth: 0.3 }, this.scene);
    frame.position.set(0, 1.7, POOL.zMax - 0.1);
    const exitMat = new StandardMaterial('exitMat', this.scene);
    exitMat.emissiveColor = new Color3(0.1, 1, 0.45);
    exitMat.disableLighting = true;
    frame.material = exitMat;
    frame.visibility = 0.5;
    edge(frame, '#19f7a0', 3);

    this.qubit = createQubitMesh(this.scene);

    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  }

  private onResize(): void {
    this.engine.resize();
  }

  setLook(x: number): void {
    this.peekX = x;
  }

  sync(state: InterferenceState): void {
    this.qubit.root.position.set(state.pos.x, 0.9, state.pos.z);
    this.qubit.root.rotation.y = state.facing;
    this.qubit.tick(state.t);

    this.poolMat.setFloat('time', state.t);
    this.poolMat.setFloat('phi', state.phi);

    const cam = this.camera.position;
    const tx = state.pos.x * 0.4 + this.peekX * 3;
    const tz = Math.max(POOL.zMin - 1, state.pos.z - 6);
    cam.x += (tx - cam.x) * 0.1;
    cam.z += (tz - cam.z) * 0.1;
    cam.y = 6;
    this.camera.setTarget(new Vector3(state.pos.x * 0.2, 0.5, state.pos.z + 7));
  }

  render(): void {
    this.scene.render();
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    this.scene.dispose();
  }
}

function edge(mesh: Mesh, hex: string, width = 2): void {
  const c = Color3.FromHexString(hex);
  mesh.enableEdgesRendering();
  mesh.edgesWidth = width;
  mesh.edgesColor = new Color4(c.r, c.g, c.b, 1);
}
